import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { IHeaderParams } from 'ag-grid-community'

import TabStore from 'stores/tab/TabStore'
import Field from 'stores/field/Field'
import Script from 'stores/script/Script'
import TelemetryStore from 'stores/telemetry/TelemetryStore'

import TypeIcon from 'components/typeIcon/TypeIcon'
import ChevronDown from 'assets/icons/chevron-down.svg'

import styles from './tableCellHeader.scss'

interface IProps extends IHeaderParams {
  field: Field
  script: Script
}

interface IState {
  wasReordered: boolean
}

class TableCellHeader extends React.Component<IProps, IState> {
  state: IState = {
    wasReordered: false,
  }

  componentDidMount() {
    this.props.column.addEventListener('movingChanged', this.handleReorder)
  }

  componentWillUnmount() {
    this.props.column.removeEventListener('movingChanged', this.handleReorder)
  }

  handleReorder = () => {
    // What we want to do is just record that a reordering happened
    // If any reordering happened, then sort needs to be canceled.
    this.setState({ wasReordered: true })
  }

  handleSort = () => {
    const { wasReordered } = this.state
    const { field, script } = this.props

    // By virtue of clicking on this cell header, consider the reordering to have ended
    this.setState({ wasReordered: false })

    if (!field || !field.isSortable) {
      return
    }

    console.log(wasReordered)
    if (wasReordered) {
      // If this cell header was reordered, do not sort
      return
    }

    TabStore.activeTab?.update({ preview: false })

    // There are three states: sort by a field (asc), sort by a field (desc), and sort by no fields
    // We want to cycle between the three, in that order

    if (script.sort.fieldId === field.id && script.sort.order === 'asc') {
      // If already sorting this field in (asc), set the state to order by same field (desc)
      script.sort.update({ fieldId: field.id, order: 'desc' })
    } else if (
      script.sort.fieldId === field.id &&
      script.sort.order === 'desc'
    ) {
      // If already sorting this field in (desc), set the state to sort by no field
      script.sort.update({ fieldId: null, order: 'asc' })
    } else {
      // If some other field (or no field) is being sorted by, set the state to order by this field (asc)
      script.sort.update({ fieldId: field.id, order: 'asc' })
    }

    script.run()

    TelemetryStore.send({
      command: 'sort_change',
      commandDetails: {
        field_type: field.type,
      },
    })
  }

  render() {
    const { field, script, displayName, columnApi, column } = this.props

    const width =
      columnApi!.getColumnState().find(s => s.colId === column.getColId())
        ?.width ?? 200

    return (
      <div
        data-cy="table__header__cell"
        className={cn(styles.container, {
          [styles.sortable]: field.isSortable,
        })}
        style={{ width }}
        title={`${field.name} (${field.type})`}
        onClick={this.handleSort}
      >
        <span data-cy="table__header__cell__title" className={styles.title}>
          {displayName}
        </span>
        <TypeIcon
          required={field.isRequired}
          array={field.isList}
          object={field.isRelation}
          string={field.isString}
          int={field.isInt || field.isBigInt}
          float={field.isFloat || field.isDecimal}
          boolean={field.isBoolean}
          dateTime={field.isDateTime}
          enumerable={field.isEnum}
        />
        <div className={styles.spacer}></div>
        <ChevronDown
          data-cy-asc={
            script.sort.fieldId === field.id && script.sort.order === 'asc'
          }
          data-cy-desc={
            script.sort.fieldId === field.id && script.sort.order === 'desc'
          }
          className={cn(styles.sortIndicator, {
            [styles.visible]: script.sort.fieldId === field.id,
            [styles.asc]: script.sort.order === 'asc',
            [styles.desc]: script.sort.order === 'desc',
          })}
        />
      </div>
    )
  }
}

export default observer(TableCellHeader)
